import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { packagePlanMap } from '../data/packagePlans';
import '../csscomponent/PackageFlow.css';
import { useAuth } from '../lib/authSession';
import { placeOrder } from '../lib/sqlAuth';

const methods = [
  { id: 'card', label: 'Credit / Debit Card' },
  { id: 'upi', label: 'UPI Payment' },
  { id: 'netbanking', label: 'Net Banking' },
];

function PaymentPage() {
  const { packageId } = useParams();
  const navigate = useNavigate();
  const plan = packagePlanMap[packageId];
  const [method, setMethod] = useState('card');
  const [errorMessage, setErrorMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    cardName: '',
    cardNumber: '',
    expiry: '',
    cvv: '',
    upiId: '',
    bank: '',
  });

  const { isAuthenticated } = useAuth();

  if (!plan) {
    return <Navigate to="/services" replace />;
  }

  if (!isAuthenticated) {
    return (
      <section className="package-flow-page">
        <div className="auth-gate">
          <p>Authentication Required</p>
          <h1>Login or signup before payment</h1>
          <span>
            To proceed with {plan.name} package payment, please use your existing account page first.
          </span>
          <div className="auth-gate-actions">
            <Link
              to="/login"
              className="flow-btn primary"
              onClick={() => sessionStorage.setItem('pendingCheckoutPath', `/payment/${plan.id}`)}
            >
              Login
            </Link>
            <Link
              to="/signup"
              className="flow-btn ghost"
              onClick={() => sessionStorage.setItem('pendingCheckoutPath', `/payment/${plan.id}`)}
            >
              Signup
            </Link>
          </div>
          <Link to={`/packages/${plan.id}`} className="auth-back-link">Back to package details</Link>
        </div>
      </section>
    );
  }

  const onChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handlePay = async (event) => {
    event.preventDefault();
    setErrorMessage('');
    try {
      setSubmitting(true);
      const result = await placeOrder({
        paymentMethod: method,
        packageItem: {
          packageId: plan.id,
          packageName: plan.name,
          priceInr: plan.price,
          quantity: 1,
        },
      });
      navigate(`/payment-success/${plan.id}`, {
        replace: true,
        state: { order: result?.order || null },
      });
    } catch (error) {
      setErrorMessage(error?.message || 'Unable to place order right now.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="package-flow-page">
      <motion.div className="flow-hero" initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}>
        <p>Payment</p>
        <h1>{plan.name} Package Checkout</h1>
        <span>Select payment method and confirm your package order.</span>
      </motion.div>

      <form className="payment-layout" onSubmit={handlePay}>
        <article className="flow-card payment-card">
          <h3>Choose Method</h3>
          <div className="method-tabs">
            {methods.map((item) => (
              <button
                type="button"
                key={item.id}
                className={`method-tab ${method === item.id ? 'active' : ''}`}
                onClick={() => setMethod(item.id)}
              >
                {item.label}
              </button>
            ))}
          </div>

          {method === 'card' && (
            <div className="method-form-grid">
              <input name="cardName" value={form.cardName} onChange={onChange} placeholder="Name on card" required />
              <input name="cardNumber" value={form.cardNumber} onChange={onChange} placeholder="Card number" required />
              <input name="expiry" value={form.expiry} onChange={onChange} placeholder="MM/YY" required />
              <input name="cvv" value={form.cvv} onChange={onChange} placeholder="CVV" required />
            </div>
          )}

          {method === 'upi' && (
            <div className="method-form-grid">
              <input name="upiId" value={form.upiId} onChange={onChange} placeholder="yourname@upi" required />
            </div>
          )}

          {method === 'netbanking' && (
            <div className="method-form-grid">
              <select name="bank" value={form.bank} onChange={onChange} required>
                <option value="">Select bank</option>
                <option value="SBI">State Bank of India</option>
                <option value="HDFC">HDFC Bank</option>
                <option value="ICICI">ICICI Bank</option>
                <option value="AXIS">Axis Bank</option>
              </select>
            </div>
          )}
        </article>

        <article className="flow-card payment-summary">
          <h3>Order Summary</h3>
          <p><strong>Plan:</strong> {plan.name}</p>
          <p><strong>Timeline:</strong> {plan.timeline}</p>
          <p><strong>Amount:</strong> INR {plan.price.toLocaleString()}</p>
          <p><strong>Billing:</strong> {plan.billingNote}</p>
          <button type="submit" className="flow-btn primary pay-btn" disabled={submitting}>
            {submitting ? 'Processing...' : 'Pay Now'}
          </button>
          <Link to={`/packages/${plan.id}`} className="flow-btn ghost">Back</Link>
          {errorMessage ? <p className="flow-meta" style={{ color: '#b42318' }}>{errorMessage}</p> : null}
        </article>
      </form>
    </section>
  );
}

export default PaymentPage;
